import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ShieldAlert, ArrowUpRight, Radar, Cpu, Wifi, CreditCard } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { APP } from '@/constants/testIds';

const riskPillClass = {
  low: 'pill-low',
  medium: 'pill-medium',
  high: 'pill-high',
  critical: 'pill-critical',
};

const Overview = () => {
  const { user } = useAuth();
  const isPaid = user?.payment_status === 'paid' || user?.role === 'admin';
  const [scans, setScans] = useState([]);
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, d, a] = await Promise.all([
        api.get('/scans').then((r) => r.data),
        api.get('/devices').then((r) => r.data),
        api.get('/alerts').then((r) => r.data),
      ]);
      setScans(s); setDevices(d); setAlerts(a);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const lastScan = scans[0];
  const activeDevices = devices.filter((d) => d.status !== 'old' && !d.blocked).length;
  const openAlerts = alerts.filter((a) => !a.resolved).length;

  return (
    <div data-testid={APP.customerDashboard} className="space-y-10">
      {!isPaid && (
        <div data-testid="payment-required-banner-overview" className="panel p-5 border-[color:var(--status-medium)] bg-[rgba(255,179,2,0.06)] flex items-start gap-4">
          <CreditCard className="text-[color:var(--status-medium)] mt-1" size={20} />
          <div className="flex-1">
            <div className="label-tag text-[color:var(--status-medium)] mb-1">// Payment Required</div>
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
              Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. Your account is
              <span className="text-[color:var(--status-medium)] font-mono"> UNPAID</span>.
              Network scans and report downloads are locked until a Supreme Web Security admin marks your account as paid.
            </p>
          </div>
          <Link to="/app/support" className="btn-secondary !py-2 !text-[10px] shrink-0">Open Ticket</Link>
        </div>
      )}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="label-tag mb-2">// Customer Portal · Overview</div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight">Operations console</h1>
          <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
            Real-time snapshot of your network: active devices, threats, and pending actions.
          </p>
        </div>
        <Link to="/app/scan" className="btn-primary w-fit">
          <Radar size={14} /> Start Security Scan
        </Link>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[color:var(--border)]">
        <Stat
          testId={APP.cardActiveDevices}
          label="Active Devices"
          value={activeDevices}
          icon={<Cpu size={16} />}
          sub={`${devices.length} total inventoried`}
        />
        <Stat
          testId={APP.cardAlerts}
          label="Open Alerts"
          value={openAlerts}
          icon={<ShieldAlert size={16} />}
          accent={openAlerts ? 'text-[color:var(--status-high)]' : ''}
          sub={`${alerts.length} lifetime alerts`}
        />
        <Stat
          testId={APP.cardRisk}
          label="Current Risk"
          value={lastScan ? lastScan.risk_level.toUpperCase() : '—'}
          icon={<Activity size={16} />}
          pill={lastScan ? riskPillClass[lastScan.risk_level] : 'pill-muted'}
          sub={lastScan ? `from scan ${lastScan.scan_id.slice(-6)}` : 'No scans yet'}
        />
        <Stat
          testId={APP.cardLastScan}
          label="Last Scan"
          value={lastScan ? new Date(lastScan.completed_at).toLocaleDateString() : '—'}
          icon={<Wifi size={16} />}
          sub={lastScan ? `${lastScan.devices_found} devices · ${lastScan.new_devices} new` : 'Run your first scan'}
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="panel p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-tag">// Recent Scans</div>
              <h2 className="text-2xl mt-2 font-light">Scan History</h2>
            </div>
            <Link to="/app/reports" className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--brand)] inline-flex items-center gap-1">
              View Reports <ArrowUpRight size={12} />
            </Link>
          </div>
          {loading ? <Loading /> : scans.length === 0 ? (
            <Empty>No scans yet. Start your first scan from the Network Scan page.</Empty>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="dtable min-w-[560px]">
                <thead><tr><th>Network</th><th>Devices</th><th>New</th><th>Risk</th><th>Date</th></tr></thead>
                <tbody>
                  {scans.slice(0, 8).map((s) => (
                    <tr key={s.scan_id}>
                      <td>{s.network_name}</td>
                      <td>{s.devices_found}</td>
                      <td className={s.new_devices > 0 ? 'text-[color:var(--brand)]' : ''}>{s.new_devices}</td>
                      <td><span className={`pill ${riskPillClass[s.risk_level]}`}>{s.risk_level}</span></td>
                      <td className="text-[color:var(--text-secondary)]">{new Date(s.started_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="panel p-6">
          <div className="label-tag">// Threat Feed</div>
          <h2 className="text-2xl mt-2 font-light mb-5">Latest Alerts</h2>
          {loading ? <Loading /> : alerts.length === 0 ? (
            <Empty>No alerts yet.</Empty>
          ) : (
            <ul className="space-y-3">
              {alerts.slice(0, 5).map((a) => (
                <li key={a.alert_id} className="border-l-2 border-[color:var(--border)] pl-3 py-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`pill ${riskPillClass[a.severity]}`}>{a.severity}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                      {new Date(a.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm">{a.title}</div>
                  <div className="text-xs text-[color:var(--text-secondary)]">{a.message}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
};

const Stat = ({ testId, label, value, sub, icon, accent, pill }) => (
  <div data-testid={testId} className="bg-[color:var(--bg-base)] p-6">
    <div className="flex items-center justify-between mb-4 text-[color:var(--text-muted)]">
      <span className="label-tag">{label}</span>{icon}
    </div>
    <div className={`text-4xl font-light tracking-tight ${accent || ''}`}>
      {pill ? <span className={`pill ${pill}`}>{value}</span> : value}
    </div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-3">{sub}</div>
  </div>
);

const Loading = () => <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--text-muted)] py-6 blink">Loading…</div>;
const Empty = ({ children }) => <div className="font-mono text-xs text-[color:var(--text-secondary)] py-6">{children}</div>;

export default Overview;
